import PropTypes from 'prop-types';
import { useState } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { alpha, styled } from '@mui/material/styles';
import {
  Alert,
  Box,
  Button,
  Card,
  Chip,
  Container,
  Divider,
  Grid,
  Link,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import Page from '../../components/Page';
import Logo from '../../components/Logo';
import Iconify from '../../components/Iconify';
import { useLanguage } from '../../context/LanguageContext';
import {
  checkEntry,
  signInWithEmailPassword,
  signInWithGoogle,
  signUpWithEmailPassword,
} from '../../services/firebaseService';
import { getVoiceTypingStatus } from '../../services/helper';
import Footer from '../../ui-component/Footer';

const RootStyle = styled('div')(({ theme }) => ({
  minHeight: '100vh',
  display: 'flex',
  flexDirection: 'column',
  background:
    'radial-gradient(circle at top left, rgba(21, 112, 78, 0.20), transparent 28rem), radial-gradient(circle at right 12%, rgba(183, 137, 43, 0.18), transparent 24rem), linear-gradient(145deg, #f5f1e8 0%, #fffdf8 45%, #ebe4d6 100%)',
}));

const HeaderStyle = styled('header')(({ theme }) => ({
  top: 0,
  zIndex: 9,
  width: '100%',
  display: 'flex',
  position: 'absolute',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: theme.spacing(3),
  [theme.breakpoints.up('md')]: {
    padding: theme.spacing(5, 6),
  },
}));

const ContentStyle = styled('div')(({ theme }) => ({
  maxWidth: 1240,
  margin: '0 auto',
  flex: 1,
  width: '100%',
  display: 'flex',
  alignItems: 'center',
  padding: theme.spacing(14, 0, 6),
}));

const SocialButton = styled(Button)(({ theme }) => ({
  minHeight: 54,
  borderRadius: 16,
  borderColor: alpha(theme.palette.grey[900], 0.16),
  backgroundColor: alpha(theme.palette.common.white, 0.92),
  color: theme.palette.grey[900],
  fontWeight: 700,
  textTransform: 'none',
  '&:hover': {
    borderColor: alpha(theme.palette.grey[900], 0.28),
    backgroundColor: theme.palette.common.white,
  },
}));

const AuthInput = styled(TextField)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    borderRadius: 16,
    backgroundColor: alpha(theme.palette.common.white, 0.9),
  },
}));

const authModes = {
  login: {
    pageTitle: 'Login',
    heroEyebrow: 'Voice typing, without the friction',
    headline: 'Login for instant access to your voice workspace.',
    subtitle:
      'Use Google for one-click access or sign in with your email and password to sync commands, language preferences, and onboarding state.',
    cardTitle: 'Welcome back',
    cardBody: 'Choose the sign-in path that matches how you work.',
    submitLabel: 'Login with email',
    switchLabel: "Don't have an account?",
    switchCta: 'Create account',
    switchHref: '/signup',
    secondaryCta: 'See product',
    secondaryHref: '/started',
  },
  signup: {
    pageTitle: 'Sign Up',
    heroEyebrow: 'Set up a better dictation workflow',
    headline: 'Create an account with Google or custom credentials.',
    subtitle:
      'Separate signup and login flows keep onboarding clear, credential management predictable, and Firebase auth easy to maintain.',
    cardTitle: 'Start free',
    cardBody: 'Create your account, save your setup, and pick up where you left off on any browser session.',
    submitLabel: 'Create account',
    switchLabel: 'Already have an account?',
    switchCta: 'Go to login',
    switchHref: '/login',
    secondaryCta: 'Back to login',
    secondaryHref: '/login',
  },
};

const stats = [
  { value: '4x', label: 'faster drafting' },
  { value: '100+', label: 'speech languages' },
  { value: '1 click', label: 'Google access' },
];

const benefits = [
  {
    icon: 'solar:microphone-3-bold-duotone',
    title: 'Separate flows',
    text: 'Dedicated login and signup experiences reduce confusion and make auth intent explicit.',
  },
  {
    icon: 'solar:shield-user-bold-duotone',
    title: 'Firebase-backed',
    text: 'Google sign-in and custom email credentials share the same auth source of truth.',
  },
  {
    icon: 'solar:command-bold-duotone',
    title: 'Profile continuity',
    text: 'Commands, preferred language, and onboarding state follow the authenticated user.',
  },
];

const waveformBars = [22, 40, 30, 56, 44, 78, 38, 70, 52, 28, 64, 46, 82, 36];

function getFriendlyError(error) {
  const code = error?.code || '';

  switch (code) {
    case 'auth/email-already-in-use':
      return 'That email already has an account. Log in instead.';
    case 'auth/invalid-email':
      return 'Enter a valid email address.';
    case 'auth/user-not-found':
    case 'auth/wrong-password':
      return 'Email or password is incorrect.';
    case 'auth/weak-password':
      return 'Use a password with at least 6 characters.';
    case 'auth/popup-closed-by-user':
      return 'Google sign-in was closed before it completed.';
    default:
      return error?.message || 'Authentication failed. Try again.';
  }
}

function validateCredentials(mode, values) {
  const nextErrors = {};

  if (mode === 'signup' && values.name.trim().length < 2) {
    nextErrors.name = 'Enter your full name.';
  }

  if (!values.email.trim()) {
    nextErrors.email = 'Email is required.';
  } else if (!/^\S+@\S+\.\S+$/.test(values.email.trim())) {
    nextErrors.email = 'Enter a valid email address.';
  }

  if (!values.password) {
    nextErrors.password = 'Password is required.';
  } else if (values.password.length < 6) {
    nextErrors.password = 'Password must be at least 6 characters.';
  }

  return nextErrors;
}

export default function AuthExperience({ mode = 'login' }) {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [formValues, setFormValues] = useState({ name: '', email: '', password: '' });
  const [formErrors, setFormErrors] = useState({});
  const [authError, setAuthError] = useState('');
  const [authNotice, setAuthNotice] = useState('');
  const [isGoogleLoading, setGoogleLoading] = useState(false);
  const [isCredentialLoading, setCredentialLoading] = useState(false);

  const copy = authModes[mode] || authModes.login;
  const isSignup = mode === 'signup';
  const loginTitle = t('login.title') || copy.pageTitle;
  const pageTitle = isSignup ? copy.pageTitle : loginTitle;
  const loginMainTitle = t('login.main.title') || copy.headline;

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    setFormValues((current) => ({ ...current, [name]: value }));
    setFormErrors((current) => ({ ...current, [name]: '' }));
    setAuthError('');
  };

  const completeAuthFlow = async (firebaseUser) => {
    const userProfile = await checkEntry('users', firebaseUser.email);
    const { message, hasCapability } = await getVoiceTypingStatus();

    if (!hasCapability && message) {
      setAuthNotice(message);
    } else {
      setAuthNotice('');
    }

    if (userProfile.onboarded && hasCapability) {
      navigate('/dashboard/app', { replace: true });
      return;
    }

    navigate(userProfile.onboarded ? '/dashboard/app' : '/onboard/permission', { replace: true });
  };

  const handleGoogleAuth = async () => {
    setAuthError('');
    setAuthNotice('');
    setGoogleLoading(true);

    try {
      const result = await signInWithGoogle();
      await completeAuthFlow(result.user);
    } catch (error) {
      setAuthError(getFriendlyError(error));
    } finally {
      setGoogleLoading(false);
    }
  };

  const handleCredentialAuth = async (event) => {
    event.preventDefault();
    const validationErrors = validateCredentials(mode, formValues);
    setFormErrors(validationErrors);
    setAuthError('');
    setAuthNotice('');

    if (Object.keys(validationErrors).length) {
      return;
    }

    setCredentialLoading(true);

    try {
      const email = formValues.email.trim();
      const password = formValues.password;
      const result = isSignup
        ? await signUpWithEmailPassword({
            name: formValues.name.trim(),
            email,
            password,
          })
        : await signInWithEmailPassword(email, password);

      await completeAuthFlow(result.user);
    } catch (error) {
      setAuthError(getFriendlyError(error));
    } finally {
      setCredentialLoading(false);
    }
  };

  return (
    <Page title={pageTitle}>
      <RootStyle>
        <HeaderStyle>
          <Logo />
          <Button component={RouterLink} to={copy.secondaryHref} variant="outlined">
            {copy.secondaryCta}
          </Button>
        </HeaderStyle>

        <Container>
          <ContentStyle>
            <Grid container spacing={4} alignItems="center">
              <Grid item xs={12} md={7}>
                <Stack spacing={4}>
                  <Box>
                    <Chip
                      label={copy.heroEyebrow}
                      color="secondary"
                      sx={{ mb: 2, fontWeight: 800 }}
                    />
                    <Typography
                      component="h1"
                      sx={{
                        fontSize: { xs: 40, md: 68 },
                        fontWeight: 800,
                        lineHeight: 1.02,
                        maxWidth: 760,
                      }}
                    >
                      {copy.headline}
                    </Typography>
                    <Typography
                      sx={{
                        color: 'text.secondary',
                        lineHeight: 1.8,
                        fontSize: 18,
                        mt: 2.5,
                        maxWidth: 700,
                      }}
                    >
                      {isSignup ? copy.subtitle : loginMainTitle}
                    </Typography>
                  </Box>

                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                    {stats.map((stat) => (
                      <Card
                        key={stat.label}
                        sx={{
                          flex: 1,
                          p: 2,
                          boxShadow: 0,
                          border: (theme) => `1px solid ${theme.palette.divider}`,
                          bgcolor: alpha('#FFFFFF', 0.72),
                        }}
                      >
                        <Typography variant="h3">{stat.value}</Typography>
                        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                          {stat.label}
                        </Typography>
                      </Card>
                    ))}
                  </Stack>

                  <Card
                    sx={{
                      p: { xs: 2.5, md: 3 },
                      color: 'common.white',
                      background: 'linear-gradient(135deg, #121212 0%, #1f2d26 58%, #0e5a3d 100%)',
                      boxShadow: '0 24px 60px rgba(18,18,18,0.18)',
                    }}
                  >
                    <Stack spacing={2.5}>
                      <Stack direction="row" justifyContent="space-between" alignItems="center">
                        <Stack direction="row" alignItems="center" spacing={1}>
                          <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: 'error.main' }} />
                          <Typography variant="subtitle2" sx={{ color: 'common.white' }}>
                            Auth + Voice Typing
                          </Typography>
                        </Stack>
                        <Chip
                          label={isSignup ? 'Separate signup' : 'Separate login'}
                          size="small"
                          sx={{ bgcolor: alpha('#FFFFFF', 0.12), color: 'common.white', fontWeight: 700 }}
                        />
                      </Stack>
                      <Box sx={{ p: 2, borderRadius: 2, bgcolor: alpha('#FFFFFF', 0.08), border: `1px solid ${alpha('#FFFFFF', 0.12)}` }}>
                        <Typography sx={{ color: alpha('#FFFFFF', 0.64), fontStyle: 'italic' }}>
                          “draft a clear follow-up and save my setup for next time”
                        </Typography>
                      </Box>
                      <Stack direction="row" spacing={0.75} alignItems="center" sx={{ height: 74 }}>
                        {waveformBars.map((height, index) => (
                          <Box key={index} sx={{ width: 9, height, borderRadius: 99, bgcolor: '#9FE870' }} />
                        ))}
                      </Stack>
                      <Box sx={{ p: 2, borderRadius: 2, bgcolor: 'common.white', color: 'text.primary' }}>
                        <Typography variant="subtitle2" sx={{ mb: 0.75 }}>
                          Reliable auth, cleaner onboarding
                        </Typography>
                        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                          Separate email and Google flows remove ambiguity, while Firebase keeps profile state unified.
                        </Typography>
                      </Box>
                    </Stack>
                  </Card>
                </Stack>
              </Grid>

              <Grid item xs={12} md={5}>
                <Card sx={{ p: { xs: 3, md: 4 }, borderRadius: 4, boxShadow: '0 22px 60px rgba(23,23,23,0.12)' }}>
                  <Stack spacing={3}>
                    <Box>
                      <Typography variant="h3" gutterBottom>
                        {copy.cardTitle}
                      </Typography>
                      <Typography sx={{ color: 'text.secondary', lineHeight: 1.8 }}>
                        {copy.cardBody}
                      </Typography>
                    </Box>

                    <SocialButton
                      fullWidth
                      variant="outlined"
                      onClick={handleGoogleAuth}
                      disabled={isGoogleLoading || isCredentialLoading}
                      startIcon={<Iconify icon="flat-color-icons:google" width={22} />}
                    >
                      {isGoogleLoading ? 'Connecting Google...' : `Continue with Google`}
                    </SocialButton>

                    <Stack direction="row" alignItems="center" spacing={2}>
                      <Divider sx={{ flex: 1 }} />
                      <Typography variant="caption" sx={{ letterSpacing: 1.4, color: 'text.secondary' }}>
                        OR USE EMAIL
                      </Typography>
                      <Divider sx={{ flex: 1 }} />
                    </Stack>

                    {authError ? <Alert severity="error">{authError}</Alert> : null}
                    {authNotice ? <Alert severity="warning">{authNotice}</Alert> : null}

                    <Box component="form" noValidate onSubmit={handleCredentialAuth}>
                      <Stack spacing={2}>
                        {isSignup ? (
                          <AuthInput
                            label="Full name"
                            name="name"
                            value={formValues.name}
                            onChange={handleInputChange}
                            error={!!formErrors.name}
                            helperText={formErrors.name}
                          />
                        ) : null}

                        <AuthInput
                          label="Email address"
                          name="email"
                          type="email"
                          value={formValues.email}
                          onChange={handleInputChange}
                          error={!!formErrors.email}
                          helperText={formErrors.email}
                        />

                        <AuthInput
                          label="Password"
                          name="password"
                          type="password"
                          value={formValues.password}
                          onChange={handleInputChange}
                          error={!!formErrors.password}
                          helperText={formErrors.password || (isSignup ? 'Use at least 6 characters.' : ' ')}
                        />

                        <Button
                          fullWidth
                          size="large"
                          type="submit"
                          variant="contained"
                          disabled={isGoogleLoading || isCredentialLoading}
                          sx={{ minHeight: 54, borderRadius: 2.5, fontWeight: 800 }}
                        >
                          {isCredentialLoading ? 'Please wait...' : copy.submitLabel}
                        </Button>
                      </Stack>
                    </Box>

                    <Stack spacing={1.5}>
                      {benefits.map((benefit) => (
                        <Stack key={benefit.title} direction="row" spacing={1.5}>
                          <Box
                            sx={{
                              width: 40,
                              height: 40,
                              borderRadius: 2,
                              bgcolor: 'background.neutral',
                              color: 'secondary.dark',
                              display: 'grid',
                              placeItems: 'center',
                              flexShrink: 0,
                            }}
                          >
                            <Iconify icon={benefit.icon} width={22} />
                          </Box>
                          <Box>
                            <Typography variant="subtitle2">{benefit.title}</Typography>
                            <Typography variant="body2" sx={{ color: 'text.secondary', lineHeight: 1.7 }}>
                              {benefit.text}
                            </Typography>
                          </Box>
                        </Stack>
                      ))}
                    </Stack>

                    <Divider />

                    <Typography variant="body2" sx={{ color: 'text.secondary', textAlign: 'center' }}>
                      {copy.switchLabel}{' '}
                      <Link component={RouterLink} to={copy.switchHref} underline="hover" sx={{ fontWeight: 700 }}>
                        {copy.switchCta}
                      </Link>
                    </Typography>

                    <Typography variant="caption" sx={{ color: 'text.secondary', textAlign: 'center', lineHeight: 1.7 }}>
                      By continuing, you agree to our{' '}
                      <Link component={RouterLink} to="/terms" underline="hover">
                        Terms of Service
                      </Link>{' '}
                      and{' '}
                      <Link component={RouterLink} to="/privacy" underline="hover">
                        Privacy Policy
                      </Link>
                      .
                    </Typography>
                  </Stack>
                </Card>
              </Grid>
            </Grid>
          </ContentStyle>
        </Container>
        <Footer />
      </RootStyle>
    </Page>
  );
}

AuthExperience.propTypes = {
  mode: PropTypes.oneOf(['login', 'signup']),
};
